/**
 * BundleModal (hardened)
 * Goals:
 *  - Add main + bundle children reliably
 *  - Remove existing bundle lines reliably (by line-item key via cart/change.js)
 *  - Verify cart state after add; re-add missing lines if needed
 *  - Prevent double-submit and stop other click/submit handlers
 *
 * Notes:
 *  - Assumes your theme marks existing bundle lines with [data-bundle-item] and data-line-item-key
 *  - Assumes selected product cards contain input[name="id"] with the child variant id
 *  - Assumes kit/free ids exist in DOM as numeric text in #kit-1-id, #kit-2-id, #free-id (or omit if not present)
 */

class BundleModal {
  constructor() {
    // HHP Customisation START
    this.trackedProductIds = [];
    // HHP Customisation END

    this.modal = document.getElementById('bundleModal');
    this.overlay = document.querySelector('.bundle-modal-overlay');
    this.title = this.modal?.querySelector('#bundleModalTitle');
    this.progress = this.modal?.querySelector('.bundle-progress');
    this.formProgress = document.querySelector('.bundle-form-progress-bar .bundle-progress');
    this.toggleButtons = this.modal?.querySelectorAll('.bundle-product-toggle') || [];
    this.closeButton = this.modal?.querySelector('[data-bundle-close]');

    this.selectedProducts = [];
    this.MAX_SELECTIONS = 6;

    // HHP Customisation START
    this.mainForm = document.querySelector('form[action^="/cart/add"]');
    // HHP Customisation END

    this.addToCartButton = document.querySelector('[data-add-to-cart-bundle]');
    this.addToBundleButton = document.querySelector('[data-add-to-bundle]');

    this.originalButtonText = this.addToCartButton
      ? this.addToCartButton.querySelector('[data-add-to-cart-text]')?.textContent || ''
      : '';
    this.originalBundleButtonText = this.addToBundleButton
      ? this.addToBundleButton.querySelector('[data-add-to-bundle-text]')?.textContent || ''
      : '';
    this.originalBundleButtonHTML = this.addToBundleButton
      ? this.addToBundleButton.querySelector('[data-add-to-bundle-text]')?.innerHTML || ''
      : '';

    this.bundleSlots = document.querySelectorAll('.bundle-product-slot');
    this.selectedCounter = document.querySelector('.bundle-product-toggle-text-count span');
    this.mainCountTextElement = document.querySelector('.bundle-product-toggle-text-count');
    this.modalCountTextElement = document.querySelector('.bundle-modal-header .bundle-product-toggle-text-count');

    this.currentSlot = null;

    // 슬롯별 선택된 제품 추적
    this.slotSelections = {};

    // Submission guard
    this.isSubmitting = false;

    this.init();
    this.restoreSelectedState();

    this.updateSlotVisibility();

    // 제거 버튼 이벤트 리스너 추가
    this.bundleSlots.forEach((slot) => {
      const removeButton = slot.querySelector('.bundle-slot-remove');
      if (removeButton) {
        removeButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.removeSelection(parseInt(slot.dataset.slot, 10));
        });
      }
    });

    // Swipers (keep your existing behavior)
    // Swiper bu sayfada yuklu olmayabilir; yoksa slot mantigi cokmesin diye guard'la.
    if (typeof Swiper !== 'undefined') {
      this.headerSlider = new Swiper('.bundle-modal-header .swiper', {
        slidesPerView: 'auto',
        spaceBetween: 10,
        watchSlidesProgress: true
      });

      this.collectionsSlider = new Swiper('.bundle-collections .swiper', {
        slidesPerView: 'auto',
        spaceBetween: 10,
        watchSlidesProgress: true,
        slidesOffsetBefore: 15,
        slidesOffsetAfter: 15
      });
    }

    // Collection links
    this.modal?.querySelectorAll('.bundle-collections a').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchCollection(link.dataset.collectionHandle, link.dataset.collectionId);
      });
    });

    this.initializeFirstCollection();

    // Overlay click closes modal
    this.overlay?.addEventListener('click', (e) => {
      if (e.target === this.overlay && e.currentTarget === this.overlay) {
        this.close();
      }
    });

    // Schema metinlerini uygula (başlık ve buton)
    this.applySchemaTexts();

    // Sepette "Kaldır" tıklanınca bundle ise tek seferde tüm satırları sil (tema tek satır siliyordu, yetim kalıyordu)
    document.addEventListener(
      'click',
      (e) => {
        const removeLink = e.target.closest && e.target.closest('[data-item-remove]');
        if (!removeLink) return;
        const row = removeLink.closest('[data-cart-item]');
        const bundleId = row && row.getAttribute && row.getAttribute('data-bundle-id');
        if (!bundleId) return;
        e.preventDefault();
        e.stopPropagation();
        this.removeBundleByBundleId(bundleId);
      },
      true
    );
    // Yetim kalan bundle satırlarını temizle: tema sepet güncelledikten sonra + sayfa ilk yüklendiğinde
    document.addEventListener('theme:cart:added', () => this.removeOrphanedBundleChildren());
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.removeOrphanedBundleChildren(), 800);
      });
    } else {
      setTimeout(() => this.removeOrphanedBundleChildren(), 800);
    }
  }

  applySchemaTexts() {
    const headingWrap = document.querySelector('.bundle-product-toggle-text[data-bundle-heading]');
    const headingEl = headingWrap?.querySelector('.bundle-heading-text');
    if (headingEl && headingWrap?.dataset.bundleHeading) {
      headingEl.textContent = headingWrap.dataset.bundleHeading;
    }
    const btnSpan = document.querySelector('[data-add-to-bundle-text][data-bundle-button-text]');
    if (btnSpan?.dataset.bundleButtonText) {
      const schemaText = btnSpan.dataset.bundleButtonText;
      btnSpan.textContent = schemaText;
      btnSpan.innerHTML = schemaText;
      this.originalBundleButtonText = schemaText;
      this.originalBundleButtonHTML = schemaText;
    }
  }

  getBundleButtonText() {
    return this.originalBundleButtonHTML
      || document.querySelector('[data-add-to-bundle-text][data-bundle-button-text]')?.dataset?.bundleButtonText
      || 'Tasarımını Seç';
  }

  // ---------------------------
  // Low-level helpers
  // ---------------------------
  safeParseInt(value) {
    const n = parseInt(String(value ?? '').trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  getText(selector) {
    const el = document.querySelector(selector);
    return el ? (el.textContent || '').trim() : '';
  }

  getHTML(selector) {
    const el = document.querySelector(selector);
    return el ? (el.innerHTML || '').trim() : '';
  }

  getSrc(selector) {
    const el = document.querySelector(selector);
    return el && el.src ? el.src : '';
  }

  normalizeImageUrl(url) {
    if (!url) return '';
    return url.replace(/_(\d+x\d+|200x_small)\.(jpg|jpeg|png|gif|webp)/i, '.$2');
  }

  setLoading(isLoading) {
    const btn = this.addToCartButton || this.mainForm?.querySelector('[data-add-to-cart-bundle]');
    if (!btn) return;
    btn.classList.toggle('btn--loading', isLoading);
    btn.disabled = !!isLoading;
  }

  async shopifyPost(path, payload) {
    const res = await fetch(window.Shopify.routes.root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });

    let data = null;
    try {
      data = await res.json();
    } catch (_) {}

    // 🔎 Keep this while diagnosing; remove later if noisy

    if (!res.ok) {
      const msg = data?.description || data?.message || `Request failed: ${path} (${res.status})`;
      const err = new Error(msg);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  async getCart() {
    const res = await fetch(window.Shopify.routes.root + 'cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    const cart = await res.json();
    return cart;
  }

  countVariantInCart(cart, variantId, bundleId = null) {
    let total = 0;
    for (const line of cart.items || []) {
      if (line.id === variantId) {
        if (!bundleId) {
          total += line.quantity;
        } else {
          const props = line.properties || {};
          if (String(props._bundle_id || '') === String(bundleId)) total += line.quantity;
        }
      }
    }
    return total;
  }

  // ---------------------------
  // Init / bindings
  // ---------------------------
  bindToggleButtons(container) {
    const buttons = container.querySelectorAll('.bundle-product-toggle');
    buttons.forEach((button) => {
      // Prevent multiple listeners by cloning
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleProduct(newButton);
      });
    });
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.bindToggleButtons(this.modal);

    this.closeButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.close();
    });

    if (this.addToCartButton) {
      this.addToCartButton.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.handleSubmit();
        },
        true
      );
    }

    if (this.mainForm) {
      this.mainForm.addEventListener(
        'submit',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.handleSubmit();
        },
        true
      );
    }

    if (this.addToBundleButton) {
      this.addToBundleButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      });
    }

    this.modal?.querySelectorAll('.bundle-product-card').forEach((card) => {
      const variantInput = card.querySelector('input[name="id"]');
      if (variantInput) card.dataset.variantId = variantInput.value;
    });
  }

  restoreSelectedState() {
    this.selectedProducts.forEach((productId) => {
      const productCard = this.modal?.querySelector(`[data-product-id="${productId}"]`);
      if (productCard) productCard.classList.add('selected');
    });
    this.updateProgress();
    this.updateAddToCartButton();
  }

  updateAddToCartButton() {
    if (this.addToCartButton) {
      const textElement = this.addToCartButton.querySelector('[data-add-to-cart-text]');
      if (textElement) textElement.textContent = this.originalButtonText;
    }
    if (this.addToBundleButton) {
      const textElement = this.addToBundleButton.querySelector('[data-add-to-bundle-text]');
      if (textElement) textElement.innerHTML = this.getBundleButtonText();
    }
  }

  /**
   * Sepette sadece ana satır silindiğinde kalan child satırları (yetim) temizler.
   * Sayfa yüklenince ve theme:cart:added sonrası çalır.
   */
  async removeOrphanedBundleChildren() {
    try {
      const cart = await this.getCart();
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;
      const parentBundleIds = new Set();
      cart.items.forEach((item) => {
        const p = item.properties || {};
        if (p.__bundle === 'true' && p._bundle_id) parentBundleIds.add(String(p._bundle_id));
      });
      const orphanKeys = cart.items
        .filter((item) => {
          const p = item.properties || {};
          const bid = p._bundle_id;
          return bid && p.__bundle !== 'true' && !parentBundleIds.has(String(bid));
        })
        .map((item) => item.key)
        .filter(Boolean);
      if (orphanKeys.length === 0) return;
      for (const key of orphanKeys) {
        await this.shopifyPost('cart/change.js', { id: key, quantity: 0 });
      }
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    } catch (e) {
      console.error('Bundle orphan cleanup failed', e);
    }
  }

  /**
   * Bir bundle_id'ye ait tüm satırları (ana + child) sepetten siler. "Kaldır" tıklanınca tek seferde tam silme.
   */
  async removeBundleByBundleId(bundleId) {
    if (!bundleId) return;
    try {
      const cart = await this.getCart();
      if (!cart || !Array.isArray(cart.items)) return;
      const keysToRemove = cart.items
        .filter((item) => (item.properties || {})._bundle_id === String(bundleId))
        .map((item) => item.key)
        .filter(Boolean);
      if (!keysToRemove.length) return;
      for (const key of keysToRemove) {
        await this.shopifyPost('cart/change.js', { id: key, quantity: 0 });
      }
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    } catch (e) {
      console.error('Bundle remove failed', e);
    }
  }

  // ---------------------------
  // Remove existing bundles (correct by line-item key)
  // ---------------------------
  async removeExistingBundlesByKey() {
    // Sepette arka planda kalmış (DOM'da görünmese bile) aynı ürüne ait
    // bundle satırlarını temizle. Farklı bundle ürünlerinin satırlarına
    // dokunmamak için, mevcut ürünün variant ID'leriyle eşleşen ana
    // (__bundle:'true') satırları referans alıp sadece o _bundle_id'lere
    // bağlı child'ları sil.
    try {
      const cart = await this.getCart();
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;

      const currentVariantIds = new Set();
      const bundleDataEl = document.querySelector('#bundle-variant-data');
      if (bundleDataEl) {
        try {
          const bundleData = JSON.parse(bundleDataEl.textContent);
          Object.keys(bundleData || {}).forEach((id) => currentVariantIds.add(String(id)));
        } catch (e) {}
      }

      const bundleIdsToRemove = new Set();
      for (const item of cart.items) {
        const props = item.properties || {};
        if (
          props.__bundle === 'true' &&
          props._bundle_id &&
          currentVariantIds.has(String(item.id))
        ) {
          bundleIdsToRemove.add(String(props._bundle_id));
        }
      }

      if (bundleIdsToRemove.size === 0) return;

      const keysToRemove = cart.items
        .filter(
          (item) =>
            item.properties &&
            item.properties._bundle_id &&
            bundleIdsToRemove.has(String(item.properties._bundle_id))
        )
        .map((item) => item.key)
        .filter(Boolean);

      for (const key of keysToRemove) {
        await this.shopifyPost('cart/change.js', { id: key, quantity: 0 });
      }
    } catch (e) {
      console.error('Failed to remove existing bundle lines from cart', e);
    }
  }

  // ---------------------------
  // Submit flow: add + verify + repair
  // ---------------------------
  async handleSubmit() {
    if (this.isSubmitting) return;
    if (!this.mainForm) {
      console.error('mainForm not found');
      return;
    }

    this.isSubmitting = true;
    this.setLoading(true);

    try {
      const formData = new FormData(this.mainForm);
      // Eğer bundle selector kullanılıyorsa, seçili radyodan variant id'yi al
      let mainVariantId = null;
      const bundleRadio = document.querySelector('.bundle-selector__input:checked');
      if (bundleRadio) {
        mainVariantId = this.safeParseInt(bundleRadio.value);

        // Form içindeki gerçek hidden id inputunu da senkronize et (tema/checkout için)
        const hiddenIdInput = this.mainForm.querySelector('input[name="id"]');
        if (hiddenIdInput && mainVariantId) {
          hiddenIdInput.value = String(mainVariantId);
        }
      } else {
        mainVariantId = this.safeParseInt(formData.get('id'));
      }
      const quantity = this.safeParseInt(formData.get('quantity')) || 1;

      if (!mainVariantId) throw new Error('Main variant id missing/invalid');

      // Remove old bundle lines first (by key)
      await this.removeExistingBundlesByKey();

      // Add new bundle and verify
      await this.addNewBundleProductsVerified(mainVariantId, quantity);

      // Reset UI state after success
      this.resetAllSelections();

      // Tema davranışına uy: sayfada kal, cart drawer vs. kendi ayarına göre çalışsın
      try {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      } catch (e) {
      }
    } catch (error) {
      console.error('Bundle submit failed:', error);
      alert(error?.message || 'Sorry, something went wrong adding your bundle.');
    } finally {
      this.setLoading(false);
      this.isSubmitting = false;
    }
  }

  buildBundleProperties(bundleId) {
    const properties = {
      '__history': '[]',
      '__bundle': 'true',
      '_bundle_id': bundleId
    };

    // Selected designs into properties
    if (Array.isArray(this.selectedProducts)) {
      this.selectedProducts.forEach((productId, index) => {
        const productCard = this.modal?.querySelector(`[data-product-id="${productId}"]`);
        if (!productCard) return;

        const sku = productCard.dataset.sku || '';
        const variantId = productCard.dataset.variantId || '';
        const productImage = productCard.querySelector('.bundle-product-image img');
        const productTitle = productCard.querySelector('.bundle-product-info h3');

        properties[`Tasarım ${index + 1}`] = productTitle ? productTitle.textContent.trim() : '';
        properties[`_bundle_image_${index + 1}`] = productImage?.src ? this.normalizeImageUrl(productImage.src) : '';
        properties[`_sku${index + 1}`] = sku;
        properties[`_variant_id_${index + 1}`] = variantId;
      });

      for (let i = this.selectedProducts.length; i < this.MAX_SELECTIONS; i++) {
        properties[`Tasarım ${i + 1}`] = '';
        properties[`_bundle_image_${i + 1}`] = '';
        properties[`_sku${i + 1}`] = '';
        properties[`_variant_id_${i + 1}`] = '';
      }
    }

    // Kits / gift metadata (safe; doesn’t impact item ids)
    const kit1Title = this.getHTML('#kit-1-title');
    const kit1Img = this.getSrc('#kit-1-img');
    const kit1Id = this.getText('#kit-1-id');
    if (kit1Title) properties['Başlangıç Seti 1'] = kit1Title;
    if (kit1Img) properties['_bundle_image_kit1'] = kit1Img;
    if (kit1Id) properties['_kit1_id'] = kit1Id;

    const kit2Title = this.getHTML('#kit-2-title');
    const kit2Img = this.getSrc('#kit-2-img');
    const kit2Id = this.getText('#kit-2-id');
    if (kit2Title) properties['Başlangıç Seti 2'] = kit2Title;
    if (kit2Img) properties['_bundle_image_kit2'] = kit2Img;
    if (kit2Id) properties['_kit2_id'] = kit2Id;

    if (window.FREE_GIFT_VARIANT_ID) {
      const freeTitle = this.getHTML('#free-title');
      const freeImg = this.getHTML('#free-img');
      const freeSku = this.getHTML('#free-sku');
      if (freeTitle) properties['Hediye'] = freeTitle;
      if (freeImg) properties['_free_gift_image'] = freeImg;
      if (freeSku) properties['_free_gift_sku'] = freeSku;
    }

    return properties;
  }

  buildItems(mainVariantId, quantity, bundleId, properties) {
    // Sadece ana ürün line item. Tasarımlar, kit, hediye properties içinde.
    // Checkout'ta tek satır görünür.
    return [
      {
        id: mainVariantId,
        quantity,
        properties
      }
    ];
  }

  buildExpectedMap(mainVariantId, quantity, bundleId) {
    // variantId -> { qty, isMain }
    const expected = new Map();
    expected.set(mainVariantId, { qty: quantity, isMain: true });

    // children: qty always 1 each (but allow duplicates)
    this.selectedProducts.forEach((productId) => {
      const card = this.modal?.querySelector(`[data-product-id="${productId}"]`);
      const vid = this.safeParseInt(card?.querySelector('input[name="id"]')?.value);
      if (!vid) return;
      const cur = expected.get(vid);
      expected.set(vid, { qty: (cur?.qty || 0) + 1, isMain: false, bundleId });
    });

    const kit1 = this.safeParseInt(this.getText('#kit-1-id'));
    if (kit1) {
      const cur = expected.get(kit1);
      expected.set(kit1, { qty: (cur?.qty || 0) + 1, isMain: false, bundleId });
    }

    const kit2 = this.safeParseInt(this.getText('#kit-2-id'));
    if (kit2) {
      const cur = expected.get(kit2);
      expected.set(kit2, { qty: (cur?.qty || 0) + 1, isMain: false, bundleId });
    }

    if (window.FREE_GIFT_VARIANT_ID) {
      const free = this.safeParseInt(this.getText('#free-id')) || this.safeParseInt(window.FREE_GIFT_VARIANT_ID);
      if (free) {
        const cur = expected.get(free);
        expected.set(free, { qty: (cur?.qty || 0) + 1, isMain: false, bundleId });
      }
    }

    return expected;
  }

  async addNewBundleProductsVerified(mainVariantId, quantity) {
    const bundleId = `bundle-${Date.now()}`;
    const properties = this.buildBundleProperties(bundleId);

    // keep hidden inputs in sync (optional)
    try {
      if (this.mainForm) {
        Object.entries(properties).forEach(([key, value]) => {
          let input = this.mainForm.querySelector(`input[name="properties[${key}]"]`);
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = `properties[${key}]`;
            this.mainForm.appendChild(input);
          }
          input.value = value;
        });
      }
    } catch (e) {
    }

    const items = this.buildItems(mainVariantId, quantity, bundleId, properties);

    // Artık sadece tek bir line item ekliyoruz ve ek sonrası ekstra doğrulama/repair yapmıyoruz.
    await this.shopifyPost('cart/add.js', { items });
  }

  // ---------------------------
  // Modal UI
  // ---------------------------
  open() {
    if (!this.modal) return;
    // Modal ve overlay'ı body'ye taşı: header/announcement stacking context'inden çıkar, her zaman en üstte ve tıklanabilir olsun
    if (this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
      if (this.overlay) document.body.appendChild(this.overlay);
    }
    this.modal.hidden = false;
    this.modal.classList.add('bundle-product-modal--open');
    document.body.classList.add('bundle-drawer-open');
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '15px';
    this.overlay?.classList.remove('hidden');
    this.restoreSelectedState();
    const slotLabel = this.getSlotLabel();
    const slots = this.modal.querySelectorAll('.swiper-wrapper .bundle-product-slot');
    slots.forEach((slot, index) => {
      const title = slot.querySelector('.bundle-slot-title');
      if (title) title.textContent = this.getSlotTitle(slotLabel, index + 1);
    });
  }

  getSlotLabel() {
    const el = document.querySelector('[data-bundle-slot-label]');
    return (el && el.dataset.bundleSlotLabel) ? el.dataset.bundleSlotLabel : 'Design';
  }

  getSlotTitle(label, index) {
    if (!label || typeof label !== 'string') return `Design ${index}`;
    const trimmed = label.trim();
    if (trimmed.toLowerCase().startsWith('count.')) {
      const rest = trimmed.slice(6).trim();
      return rest ? `${index}. ${rest}` : `${index}`;
    }
    return `${trimmed} ${index}`;
  }

  close() {
    if (!this.modal) return;
    this.modal.hidden = true;
    this.modal.classList.remove('bundle-product-modal--open');
    document.body.classList.remove('bundle-drawer-open');
    document.body.style.overflow = 'auto';
    document.body.style.paddingRight = '0';
    this.overlay?.classList.add('hidden');
    this.updateAddToCartButton();
  }

  updateProgress() {
    const count = this.selectedProducts.length;
    const percentage = (count / this.MAX_SELECTIONS) * 100;

    const countText = `<span>${count}</span>/${this.MAX_SELECTIONS} Seçildi`;
    if (this.mainCountTextElement) this.mainCountTextElement.innerHTML = countText;
    if (this.modalCountTextElement) this.modalCountTextElement.innerHTML = countText;

    if (this.progress) this.progress.style.width = `${percentage}%`;
    if (this.formProgress) this.formProgress.style.width = `${percentage}%`;
  }

  updateCounter() {
    const count = this.selectedProducts.length;
    if (this.selectedCounter) this.selectedCounter.textContent = String(count);
  }

  findNextEmptySlot() {
    for (let i = 0; i < this.MAX_SELECTIONS; i++) {
      if (!this.slotSelections[i + 1]) return i + 1;
    }
    return null;
  }

  getSlotForProduct(productId) {
    for (const [slot, pid] of Object.entries(this.slotSelections)) {
      if (pid === productId) return parseInt(slot, 10);
    }
    return null;
  }

  toggleProduct(button) {
    const productId = button.dataset.productId;
    const productCard = button.closest('.bundle-product-card');

    if (this.selectedProducts.includes(productId)) {
      const slotNumber = this.getSlotForProduct(productId);
      if (slotNumber) this.removeSelection(slotNumber);
      return;
    }

    this.currentSlot = this.findNextEmptySlot();
    if (!this.currentSlot) return;

    this.selectedProducts.push(productId);

    // HHP Customisation START
    this.trackedProductIds.push(productId);
    // HHP Customisation END

    this.slotSelections[this.currentSlot] = productId;
    productCard?.classList.add('selected');

    this.updateSelectedSlot(productId);

    this.currentSlot = this.findNextEmptySlot();
    this.updateProgress();
    this.updateCounter();
    this.updateButtonState();

    if (this.selectedProducts.length === this.MAX_SELECTIONS) {
      const realButton = document.querySelector('.bundle-product-toggle-real');
      const fakeButton = document.querySelector('.bundle-product-toggle-fake');
      if (realButton && fakeButton) {
        realButton.style.display = 'block';
        fakeButton.style.display = 'none';
      }
      this.close();
    }
  }

  updateButtonState() {
    const realButton = document.querySelector('.bundle-product-toggle-real');
    const fakeButton = document.querySelector('.bundle-product-toggle-fake');
    const selectedCount = this.selectedProducts.length;

    if (selectedCount === this.MAX_SELECTIONS) {
      if (realButton) {
        realButton.style.display = 'block';
        realButton.disabled = false;
      }
      if (fakeButton) fakeButton.style.display = 'none';
    } else {
      if (realButton) realButton.style.display = 'none';
      if (fakeButton) {
        fakeButton.style.display = 'block';
        const textElement = fakeButton.querySelector('[data-add-to-bundle-text]');
        if (textElement) textElement.innerHTML = this.getBundleButtonText();
      }
    }
  }

  updateMaxSelections(selectedVariantId, variantTitle) {
    const oldMax = this.MAX_SELECTIONS;

    if (!selectedVariantId) return;

    let maxItemsFromData = null;
    const bundleDataEl = document.querySelector('#bundle-variant-data');
    if (bundleDataEl) {
      try {
        const bundleData = JSON.parse(bundleDataEl.textContent);
        const raw = bundleData[selectedVariantId] ?? bundleData[String(selectedVariantId)];
        if (raw !== undefined && raw !== null) {
          maxItemsFromData = parseInt(raw, 10);
        }
      } catch (e) {
        /* fallback to switch below */
      }
    } else {
      return;
    }

    if (maxItemsFromData != null && !isNaN(maxItemsFromData) && maxItemsFromData > 0) {
      this.MAX_SELECTIONS = maxItemsFromData;
    } else {
      switch (variantTitle) {
        case 'SAVE 30%':
          this.MAX_SELECTIONS = 1;
          break;
        case 'SAVE 35%':
          this.MAX_SELECTIONS = 2;
          break;
        case 'SAVE 40%':
          this.MAX_SELECTIONS = 3;
          break;
        default:
          this.MAX_SELECTIONS = 3;
      }
    }

    this.updateSlotVisibility();
    this.updateCounter();

    if (oldMax !== this.MAX_SELECTIONS) {
      this.updateProgress();
      this.updateButtonState();
    }
  }

  updateSlotVisibility() {
    ['', '.bundle-modal-header '].forEach((selector) => {
      const slots = document.querySelectorAll(`${selector}.bundle-product-slot`);
      slots.forEach((slot, index) => {
        if (index < this.MAX_SELECTIONS) {
          slot.classList.remove('hidden');
          const badge = slot.querySelector('.bundle-slot-badge');
          if (badge && index === 3) {
            if (this.MAX_SELECTIONS === 6) badge.classList.add('hidden');
            else badge.classList.remove('hidden');
          }
        } else {
          slot.classList.add('hidden');
        }
      });
    });
  }

  updateSelectedSlot(productId) {
    const slot = this.bundleSlots[this.currentSlot - 1];
    if (!slot) return;

    const productCard = this.modal?.querySelector(`[data-product-id="${productId}"]`);
    if (!productCard) return;

    const productImage = productCard.querySelector('.bundle-product-image img');

    const apply = (targetSlot) => {
      const targetContent = targetSlot.querySelector('.bundle-slot-content');
      const targetPlaceholder = targetSlot.querySelector('.bundle-slot-placeholder');
      const targetImage = targetContent?.querySelector('img');
      const targetTitle = targetSlot.querySelector('.bundle-slot-title');

      if (!targetImage || !productImage?.src) return;

      targetImage.src = productImage.src;
      targetImage.onload = () => {
        targetContent.classList.remove('hidden');
        targetPlaceholder.classList.add('hidden');
        targetSlot.classList.add('selected');
        if (targetTitle) targetTitle.textContent = productImage.alt || this.getSlotTitle(this.getSlotLabel(), this.currentSlot);
      };
    };

    apply(slot);

    const headerSlot = this.modal?.querySelector(
      `.bundle-modal-header .bundle-product-slot[data-slot="${this.currentSlot}"]`
    );
    if (headerSlot) apply(headerSlot);
  }

  clearSlot(slotNumber) {
    const slot = this.bundleSlots[slotNumber - 1];
    if (!slot) return;

    const content = slot.querySelector('.bundle-slot-content');
    const placeholder = slot.querySelector('.bundle-slot-placeholder');
    const image = content?.querySelector('img');
    const title = slot.querySelector('.bundle-slot-title');

    if (image) image.src = '';

    content?.classList.add('hidden');
    placeholder?.classList.remove('hidden');
    slot.classList.remove('selected');
    const slotLabel = this.getSlotLabel();
    if (title) title.textContent = this.getSlotTitle(slotLabel, slotNumber);

    const headerSlot = this.modal?.querySelector(`.bundle-modal-header .bundle-product-slot[data-slot="${slotNumber}"]`);
    if (headerSlot) {
      const headerContent = headerSlot.querySelector('.bundle-slot-content');
      const headerPlaceholder = headerSlot.querySelector('.bundle-slot-placeholder');
      const headerImage = headerContent?.querySelector('img');
      const headerTitle = headerSlot.querySelector('.bundle-slot-title');

      if (headerImage) headerImage.src = '';
      headerContent?.classList.add('hidden');
      headerPlaceholder?.classList.remove('hidden');
      headerSlot.classList.remove('selected');
      if (headerTitle) headerTitle.textContent = this.getSlotTitle(slotLabel, slotNumber);
    }

    delete this.slotSelections[slotNumber];
  }

  resetAllSelections() {
    this.selectedProducts = [];
    this.slotSelections = {};
    this.trackedProductIds = [];

    this.bundleSlots.forEach((slot, index) => {
      this.clearSlot(index + 1);
      slot.classList.remove('selected');
    });

    this.modal?.querySelectorAll('.bundle-product-card.selected').forEach((card) => {
      card.classList.remove('selected');
    });

    const realButton = document.querySelector('.bundle-product-toggle-real');
    const fakeButton = document.querySelector('.bundle-product-toggle-fake');

    if (realButton && fakeButton) {
      realButton.style.display = 'none';
      fakeButton.style.display = 'block';
      const el = fakeButton.querySelector('[data-add-to-bundle-text]');
      if (el) el.innerHTML = this.getBundleButtonText();
    }

    this.updateCounter();
    this.updateProgress();
    this.updateButtonState();
  }

  removeSelection(slotNumber) {
    const productId = this.slotSelections[slotNumber];
    if (!productId) return;

    const index = this.selectedProducts.indexOf(productId);
    if (index > -1) this.selectedProducts.splice(index, 1);

    const trackedIndex = this.trackedProductIds.indexOf(productId);
    if (trackedIndex > -1) this.trackedProductIds.splice(trackedIndex, 1);

    this.clearSlot(slotNumber);

    const productCard = this.modal?.querySelector(`[data-product-id="${productId}"]`);
    if (productCard) productCard.classList.remove('selected');

    this.currentSlot = slotNumber;

    this.updateProgress();
    this.updateCounter();
    this.updateButtonState();
    this.updateAddToCartButton();
  }

  // Collection switching: göster/gizle + ilk açılışta koleksiyonun TÜM ürünlerini tek seferde yükle
  async switchCollection(collectionHandle, collectionId) {
    this.modal?.querySelectorAll('.bundle-products-grid').forEach((grid) => grid.classList.add('hidden'));
    const selectedGrid = this.modal?.querySelector(`.bundle-products-grid[data-collection-id="${collectionId}"]`);
    if (selectedGrid) {
      selectedGrid.classList.remove('hidden');
      const handle = selectedGrid.dataset.collectionHandle || collectionHandle;
      if (selectedGrid.dataset.loaded !== 'done' && handle) await this.loadCollectionProducts(selectedGrid, handle);
    }
    this.modal?.querySelectorAll('.bundle-collections a').forEach((link) => {
      link.classList.toggle('active', link.dataset.collectionId === collectionId);
    });
  }

  // Koleksiyonun tüm ürünlerini tek istekte yükler (scroll ile kademeli yükleme kaldırıldı)
  async loadCollectionProducts(grid, collectionHandle) {
    if (grid.classList.contains('loading')) return;

    grid.classList.add('loading');
    try {
      const response = await fetch(`${window.Shopify.routes.root}collections/${collectionHandle}?view=bundle`);
      if (!response.ok) throw new Error('Network response was not ok');
      const html = await response.text();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const newProducts = tempDiv.querySelector('.bundle-products-grid');
      if (newProducts && newProducts.innerHTML.trim().length > 0) {
        grid.innerHTML = newProducts.innerHTML;
        this.bindToggleButtons(grid);
        this.restoreSelectedState();
      }
      grid.dataset.loaded = 'done';
    } catch (error) {
      console.error('Failed to load collection:', error);
    } finally {
      grid.classList.remove('loading');
    }
  }

  initializeFirstCollection() {
    const firstCollectionLink = this.modal?.querySelector('.bundle-collections a');
    if (firstCollectionLink) this.switchCollection(firstCollectionLink.dataset.collectionHandle, firstCollectionLink.dataset.collectionId);
  }
}

// Global helper (kept for compatibility)
function openBundleModal(slotNumber) {
  if (window.bundleModal) {
    window.bundleModal.currentSlot = slotNumber;
    window.bundleModal.open();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.bundleModal = new BundleModal();
});

// Extra swiper (your original)
// Swiper yoksa bu satir ReferenceError firlatip alttaki bundle-selector kodunun
// (indirim butonu -> slot sayisi) hic calismamasina yol aciyordu. Guard eklendi.
const bundle_collections_slide = (typeof Swiper !== 'undefined')
  ? new Swiper('.bundle-collections .swiper', {
      slidesPerView: 'auto',
      spaceBetween: 8,
      slidesOffsetBefore: 15,
      slidesOffsetAfter: 15,
      breakpoints: {
        768: {
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20
        }
      }
    })
  : null;

// Inventory helpers (left in place; you said availability is guaranteed)
function checkInventory(variantId, quantity) {
  const productCard = document.querySelector(`[data-variant-id="${variantId}"]`);
  if (!productCard) return true;
  const inventoryQuantity = parseInt(productCard.dataset.inventoryQuantity, 10);
  if (isNaN(inventoryQuantity)) return true;
  return inventoryQuantity >= quantity;
}

// --- Rewritten Bundle Selector Animation ---
document.addEventListener('DOMContentLoaded', () => {
  const optionsContainer = document.querySelector('.bundle-selector__options');
  if (!optionsContainer) return;

  const animationBar = optionsContainer.querySelector('.bundle-selector__animation');
  const radioInputs = optionsContainer.querySelectorAll('.bundle-selector__input');

  if (!animationBar || radioInputs.length === 0) return;

  const updateBundleUI = () => {
    let selectedRadio = optionsContainer.querySelector('.bundle-selector__input:checked');
    
    // If no radio is checked on load, default to the first one.
    if (!selectedRadio && radioInputs.length > 0) {
        radioInputs[0].checked = true;
        selectedRadio = radioInputs[0];
    }

    if (!selectedRadio) return; // Exit if still no radio found

    const selectedIndex = parseInt(selectedRadio.dataset.index) || 0;
    const selectedId = selectedRadio.value;
    const selectedTitle = selectedRadio.dataset.optionValue;
    
    // 1. Move the animation bar
    const percentage = selectedIndex * 33.33;
    animationBar.style.left = `${percentage}%`;

    // 2. Form: tek gerçek variant id (hidden input) güncelle – sepet/checkout doğru variant gitsin
    const form = optionsContainer.closest('form');
    if (form) {
      const hiddenId = form.querySelector('input[name="id"]');
      if (hiddenId) hiddenId.value = selectedId;
    }

    // 3. Fiyat alanlarını güncelle (fiyat blok formun dışında olabilir; product-info içinde ara)
    const formWrapper = optionsContainer.closest('[data-form-wrapper]') || optionsContainer.closest('product-form');
    const productSection = optionsContainer.closest('product-info') || optionsContainer.closest('[data-section-type="product-template"]') || formWrapper;
    if (formWrapper) {
      const priceCents = selectedRadio.dataset.variantPrice ? parseInt(selectedRadio.dataset.variantPrice, 10) : null;
      const compareCents = selectedRadio.dataset.variantCompare ? parseInt(selectedRadio.dataset.variantCompare, 10) : null;
      if (priceCents != null) {
        formWrapper.setAttribute('data-variant-price', String(priceCents));
        if (compareCents != null) formWrapper.setAttribute('data-variant-compare-price', String(compareCents));
      }
    }
    if (productSection) {
      const priceCents = selectedRadio.dataset.variantPrice ? parseInt(selectedRadio.dataset.variantPrice, 10) : null;
      const compareCents = selectedRadio.dataset.variantCompare ? parseInt(selectedRadio.dataset.variantCompare, 10) : null;
      const formatMoney = window.theme?.formatMoney;
      if (priceCents != null && typeof formatMoney === 'function') {
        const priceEl = productSection.querySelector('[data-product-price]');
        const compareEl = productSection.querySelector('[data-compare-price]');
        const moneyFormat = window.theme?.moneyFormat || window.theme?.moneyWithCurrencyFormat;
        if (priceEl) priceEl.textContent = formatMoney(priceCents, moneyFormat);
        if (compareEl) {
          const compareVal = (compareCents != null && compareCents > priceCents) ? compareCents : priceCents;
          compareEl.textContent = formatMoney(compareVal, moneyFormat);
          compareEl.style.display = (compareCents != null && compareCents > priceCents) ? '' : 'none';
        }

        // Saving badge (örn. "%50 İNDİRİMLİ") güncelle
        const priceOffWrapper = productSection.querySelector('.product__price--off');
        if (priceOffWrapper) {
          const badgeAmountEl = priceOffWrapper.querySelector('.product__price-badge span');
          if (compareCents != null && compareCents > priceCents) {
            const discountDiff = compareCents - priceCents;
            const discountPercentage = Math.round((discountDiff / compareCents) * 100);
            if (badgeAmountEl && !Number.isNaN(discountPercentage) && discountPercentage > 0) {
              badgeAmountEl.textContent = `${discountPercentage}%`;
            }
            priceOffWrapper.style.display = '';
          } else {
            // İndirim yoksa badge'i gizle
            priceOffWrapper.style.display = 'none';
          }
        }
      }
    }

    // 4. Max selections (slot sayısı) güncelle
    if (window.bundleModal && typeof window.bundleModal.updateMaxSelections === 'function') {
      window.bundleModal.updateMaxSelections(selectedId, selectedTitle);
    }
  };

  // Set initial state on page load
  updateBundleUI();

  // Add event listeners to update on change
  radioInputs.forEach(input => {
    input.addEventListener('change', updateBundleUI);
  });
});
